import { storeToRefs } from "pinia";
import { computed, watch } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { getLocaleText } from "shared/boot/i18n";
import {
  type ContactResourceType,
  type ContactType,
} from "shared/composables/useContact";
import {
  type SideBarContentData,
  useSideBarStore,
} from "shared/modules/SideBar/stores/useSideBarStore";
import { isAbortError } from "shared/services/api/isAbortError";
import OutreachContactService from "shared/services/api/OutreachContactService";
import features from "shared/services/features";
import { useOutreachContactStore } from "shared/stores/outreachContact";
import { type Maybe, type Nullable } from "shared/types";
import type { BaseMentionInfluencer, Source } from "shared/types/mentions";
import {
  isMentionWithInfluencer,
  isMentionWithOutlet,
} from "shared/types/mentions/guards";
import type { Influencer, Outlet } from "shared/types/outreach";

function createFakeInfluencerContact(): Influencer {
  const influencer: Influencer = {
    archived_at: null,
    avatar_url: null,
    average_facebook_engagement: null,
    average_pinterest_shares: null,
    average_reddit_engagements: null,
    average_total_engagements: null,
    average_twitter_shares: null,
    biography: "",
    business_card_titles: ["Columnist"],
    display_bio: "",
    document_id: "influencer_9999999",
    dominant_facebook_reaction: null,
    email: "ag@jacksonvilletoday.com",
    emails: [{ defaultFlag: true, email: "ag@jacksonvilletoday.com" }],
    faxes: [],
    first_name: "A.G.",
    gender: null,
    high_res_avatar_url: null,
    homepages: [],
    id: 9999999,
    influencer_addresses: [],
    influencer_business_cards: [],
    influencer_outreach_topics: [],
    influencers_outlets: [],
    internal_ids: [],
    languages: ["English"],
    last_name: "Gosta",
    location: "Jacksonville, United States of America",
    matched_outlets: [],
    middle_name: null,
    name: "A.G. Gosta",
    opted_type: "",
    outlet: null,
    outlet_names: ["New York Post", "Florida Politics", "Jacksonville Today"],
    outlets: [],
    phones: [],
    profile:
      "A.G. Gosta is a columnist and contributor at Jacksonville Today, freelance at New York Post, and journalist at Florida Politics, specializing in editorial/opinion, local news, and local politics. He is based in Jacksonville, Florida.",
    salutation: "",
    socials: [
      { defaultFlag: true, socialType: "LinkedIn", socialUrl: "#" },
      { defaultFlag: true, socialType: "Twitter", socialUrl: "#" },
    ],
    status: "Active",
    suffix: null,
    top_facebook_reaction: null,
    total_angry_reactions: 0,
    total_haha_reactions: 0,
    total_love_reactions: 0,
    total_sad_reactions: 0,
    total_wow_reactions: 0,
    twitter_user: null,
    twitter_username: "",
    type: "influencer",
    verified_by: "",
    verified_date: "",
  };

  influencer.influencer_addresses = [
    {
      address: {
        city: "Jacksonville",
        country: "United States of America",
        state: "Florida",
      },
      archived_at: null,
      default_flag: true,
      id: 1,
      influencer_id: influencer.id,
      outlet_id: null,
    },
  ];

  influencer.influencer_outreach_topics = [
    { id: 1, name: "Editorial/Opinion" },
    { id: 2, name: "Local News" },
    { id: 3, name: "Local Politics" },
  ];

  influencer.influencers_outlets = [
    {
      archived_at: null,
      id: 1,
      influencer,
      influencer_id: influencer.id,
      outlet: null,
      outlet_id: 1,
    },
    {
      archived_at: null,
      id: 2,
      influencer,
      influencer_id: influencer.id,
      outlet: null,
      outlet_id: 2,
    },
    {
      archived_at: null,
      id: 3,
      influencer,
      influencer_id: influencer.id,
      outlet: null,
      outlet_id: 3,
    },
  ];

  return influencer;
}

function createFakeOutletContact(): Outlet {
  const outlet: Outlet = {
    ad_eqv_tier: 0,
    archived_at: null,
    avatar_url: null,
    circulation: null,
    circulations: null,
    content_types: [],
    coverage: "National",
    coverages: [],
    dma_names: [],
    document_id: "outlet_9999999",
    email: "tips@floridapolitics.com",
    emails: [{ defaultFlag: true, email: "tips@floridapolitics.com" }],
    faxes: [],
    freelance_outlet_flag: null,
    frequencies: [{ frequency: "Daily", frequencyId: 226 }],
    gpod_id: 0,
    group: "Internet",
    high_res_avatar_url: null,
    homepages: [{ defaultFlag: true, homepage: "https://floridapolitics.com" }],
    id: 9999999,
    influencers_count: 0,
    influencers_outlets: [],
    internal_ids: [],
    languages: ["English"],
    location: "Tallahassee, United States of America",
    matched_influencers: [],
    mdm_id: "",
    monitoring_only_flag: false,
    name: "Gosta Politics",
    normalized_circulations: [],
    outlet_addresses: [],
    outlet_outreach_topics: [],
    outlet_type: null,
    outlet_type_id: 0,
    owner: null,
    parent_outlet_id: null,
    parent_relationship: null,
    phones: [],
    profile:
      "Gosta Politics is a digital news outlet covering Gosta government, elections, and political news. Based in Tallahassee, Florida.",
    rates: [],
    socials: [{ defaultFlag: true, socialType: "Twitter", socialUrl: "#" }],
    status: "Active",
    target_areas: [],
    type: "outlet",
    unique_visitors_per_month: null,
    verified_by: "",
    verified_date: "",
  };

  outlet.outlet_addresses = [
    {
      address: {
        city: "Tallahassee",
        country: "United States of America",
        state: "Florida",
      },
      archived_at: null,
      default_flag: true,
      id: 1,
    },
  ];

  outlet.outlet_outreach_topics = [
    { id: 1, name: "Government" },
    { id: 2, name: "Elections" },
    { id: 3, name: "State Politics" },
  ];

  return outlet;
}

export function useOutreachContactSidebar() {
  const outreachContactStore = useOutreachContactStore();

  const { isContactLoading, selectedContact, selectedContactResourceType } =
    storeToRefs(outreachContactStore);

  const sideBarStore = useSideBarStore();
  const { sideBarOpen, sideBarSlotComponent } = storeToRefs(sideBarStore);

  let contactAbortController: Nullable<AbortController> = null;

  const showRestrictMessage = computed<boolean>(
    () => !features.has("has_outreach")
  );

  function resetOutreachContactSidebar(): void {
    outreachContactStore.reset();
  }

  function showSidebar(data?: Partial<SideBarContentData>): void {
    sideBarStore.setSideBarContent({
      data,
      hideHeader: true,
      slotComponent: "OutreachContactSidebar",
      width: "576px",
    });

    sideBarStore.showSideBar();
  }

  function hideSidebar(): void {
    sideBarStore.hideSideBar();
  }

  async function loadInfluencer(
    influencer: Maybe<BaseMentionInfluencer>,
    abortController: AbortController
  ): Promise<Nullable<Influencer>> {
    if (!influencer?.id) return null;
    if (showRestrictMessage.value) return createFakeInfluencerContact();

    const contactResponse = await OutreachContactService.getInfluencer(
      influencer.id,
      {
        signal: abortController.signal,
      }
    );

    if (!contactResponse.data || !contactResponse.data.id) {
      throw new Error(getLocaleText("online_mention_card.author_not_found"));
    }

    const loadedContact = contactResponse.data;

    if (!loadedContact.type) {
      throw new Error(getLocaleText("online_mention_card.author_not_found"));
    }

    return loadedContact;
  }

  async function loadOutlet(
    outletId: number,
    abortController: AbortController
  ): Promise<Nullable<Outlet>> {
    if (showRestrictMessage.value) return createFakeOutletContact();

    try {
      const response = await OutreachContactService.getOutlet(outletId, {
        signal: abortController.signal,
      });

      return response.data;
    } catch (error) {
      if (!isAbortError(error)) {
        ErrorDialog(getLocaleText("outreach_outlet.load_outlet_error"), error);
      }

      return null;
    }
  }

  function setContact(contact: ContactType, type: ContactResourceType) {
    selectedContact.value = contact;
    selectedContactResourceType.value = type;
  }

  function abortLoadContact() {
    if (contactAbortController) {
      contactAbortController.abort();
      contactAbortController = null;
    }
  }

  async function handleClickContact(
    sourceOrInfluencer: Maybe<BaseMentionInfluencer | Source>
  ): Promise<void> {
    abortLoadContact();

    if (!sourceOrInfluencer) return;

    try {
      isContactLoading.value = true;
      contactAbortController = new AbortController();
      showSidebar();

      if (
        isMentionWithOutlet(sourceOrInfluencer) &&
        sourceOrInfluencer.outlet_id
      ) {
        const outlet = await loadOutlet(
          sourceOrInfluencer.outlet_id,
          contactAbortController
        );

        if (outlet) {
          setContact(outlet, "outlet");
        }
      }

      if (isMentionWithInfluencer(sourceOrInfluencer)) {
        const influencer = await loadInfluencer(
          sourceOrInfluencer,
          contactAbortController
        );

        if (influencer) {
          setContact(influencer, "influencer");
        }
      }

      isContactLoading.value = false;
    } catch (error) {
      if (!isAbortError(error)) {
        ErrorDialog(getLocaleText("outreach_outlet.load_outlet_error"), error);

        hideSidebar();
      }
    } finally {
      if (!contactAbortController) isContactLoading.value = false;
    }
  }

  function handlePreviewContact(
    contact: ContactType,
    type: ContactResourceType,
    data?: Partial<SideBarContentData>
  ): void {
    abortLoadContact();

    if (
      sideBarOpen.value &&
      (!sideBarSlotComponent.value ||
        sideBarSlotComponent.value === "OutreachContactSidebar") &&
      selectedContact.value?.id === contact.id &&
      type === selectedContactResourceType.value
    ) {
      resetOutreachContactSidebar();
      hideSidebar();

      return;
    }

    setContact(contact, type);
    showSidebar(data);
  }

  watch(sideBarOpen, (value) => {
    if (!value) {
      abortLoadContact();
    }
  });

  async function handleClickOutlet(id: number | string): Promise<void> {
    abortLoadContact();

    try {
      isContactLoading.value = true;
      contactAbortController = new AbortController();
      showSidebar();

      const outlet = await loadOutlet(Number(id), contactAbortController);

      if (outlet) {
        setContact(outlet, "outlet");
      }

      isContactLoading.value = false;
    } catch (error) {
      if (!isAbortError(error)) {
        ErrorDialog(getLocaleText("outreach_outlet.load_outlet_error"), error);
        hideSidebar();
      }
    } finally {
      isContactLoading.value = false;
    }
  }

  return {
    handleClickContact,
    handleClickOutlet,
    handlePreviewContact,
  };
}
